<script setup>
import { computed } from 'vue'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import { useI18n } from 'vue-i18n'

import { useWebSiteStore } from '@/stores/web-site.store'

dayjs.extend(isoWeek)

const props = defineProps({
  rangeStart: {
    type: Object,
    required: true,
  },
  rangeDays: {
    type: Number,
    required: true,
  },
  dayWidth: {
    type: Number,
    required: true,
  },
  stickyOffset: {
    type: Number,
    required: true,
  },
})

const { locale } = useI18n()
const webSiteStore = useWebSiteStore()

const HOURS_PER_DAY = 24
const DAYS_PER_WEEK = 7
const HOURS_MIN_DAY_WIDTH = 20
const HOUR_STEPS = Object.freeze([
  { minHourWidth: 2.5, step: 1, withMinutes: true },
  { minHourWidth: 1.5, step: 1, withMinutes: false },
  { minHourWidth: 0.75, step: 2, withMinutes: false },
  { minHourWidth: 0, step: 3, withMinutes: false },
])
const DAY_STACKED_MIN_WIDTH = 2.5
const DAY_CELLS_MIN_WIDTH = 1.25

const formatter = (options) => new Intl.DateTimeFormat(locale.value, options)
const formatters = computed(() => ({
  weekday: formatter({ weekday: 'short' }),
  weekdayNarrow: formatter({ weekday: 'narrow' }),
  dayFull: formatter({ weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }),
  dayMonth: formatter({ day: 'numeric', month: 'short' }),
  monthYear: formatter({ month: 'long', year: 'numeric' }),
}))

const today = dayjs()
const rangeEnd = computed(() => props.rangeStart.add(props.rangeDays, 'day'))

const MINUTES_PER_DAY = 1440
const indexOf = (date) => date.startOf('day').diff(props.rangeStart, 'day') + (date.hour() * 60 + date.minute()) / MINUTES_PER_DAY

const cellsFor = (unitStart, nextUnit, describe) => {
  const cells = []
  for (let cursor = unitStart(props.rangeStart); cursor.isBefore(rangeEnd.value); cursor = nextUnit(cursor)) {
    const next = nextUnit(cursor)
    const from = cursor.isBefore(props.rangeStart) ? props.rangeStart : cursor
    const to = next.isAfter(rangeEnd.value) ? rangeEnd.value : next
    cells.push({
      key: cursor.valueOf(),
      left: indexOf(from) * props.dayWidth,
      width: (indexOf(to) - indexOf(from)) * props.dayWidth,
      ...describe(cursor),
    })
  }
  return cells
}


const monthCells = () => cellsFor(
  day => day.startOf('month'),
  day => day.add(1, 'month'),
  day => ({ label: formatters.value.monthYear.format(day.toDate()) }),
)

const weekCells = (compact) => cellsFor(
  day => day.startOf('isoWeek'),
  day => day.add(DAYS_PER_WEEK, 'day'),
  (day) => {
    const { dayMonth } = formatters.value
    const range = `${dayMonth.format(day.toDate())} – ${dayMonth.format(day.add(DAYS_PER_WEEK - 1, 'day').toDate())}`
    return compact
      ? { label: `${day.isoWeek()}`, today: today.isSame(day, 'isoWeek') }
      : { label: `${day.isoWeek()}`, caption: range, today: today.isSame(day, 'isoWeek') }
  },
)

const dayCells = (stacked) => cellsFor(
  day => day,
  day => day.add(1, 'day'),
  (day) => {
    const { weekday, weekdayNarrow } = formatters.value
    return {
      label: day.date(),
      caption: stacked ? weekday.format(day.toDate()) : weekdayNarrow.format(day.toDate()),
      rest: webSiteStore.isRestDay(day),
      today: day.isSame(today, 'day'),
    }
  },
)

const dayTitleCells = () => cellsFor(
  day => day,
  day => day.add(1, 'day'),
  day => ({ label: formatters.value.dayFull.format(day.toDate()), rest: webSiteStore.isRestDay(day), today: day.isSame(today, 'day') }),
)

const hourCells = () => {
  const hourWidth = props.dayWidth / HOURS_PER_DAY
  const { step, withMinutes } = HOUR_STEPS.find(s => hourWidth >= s.minHourWidth)
  return cellsFor(
    day => day,
    day => day.add(step, 'hour'),
    day => ({
      label: withMinutes ? day.format('HH:mm') : day.format('HH'),
      today: !today.isBefore(day) && today.isBefore(day.add(step, 'hour')),
      rest: webSiteStore.isRestDay(day),
    }),
  )
}

const tiers = computed(() => {
  if (props.dayWidth >= HOURS_MIN_DAY_WIDTH) {
    return { top: dayTitleCells(), bottom: hourCells(), compact: true }
  }
  if (props.dayWidth >= DAY_STACKED_MIN_WIDTH) {
    return { top: weekCells(false), bottom: dayCells(true), compact: false }
  }
  if (props.dayWidth >= DAY_CELLS_MIN_WIDTH) {
    return { top: monthCells(), bottom: dayCells(false), compact: false }
  }
  return { top: monthCells(), bottom: weekCells(true), compact: true }
})

const showWeekPrefix = computed(() => props.dayWidth >= DAY_STACKED_MIN_WIDTH && props.dayWidth < HOURS_MIN_DAY_WIDTH)
</script>

<template>
  <div class="timeline-scale" :style="{ '--scale-sticky-left': `${props.stickyOffset}rem` }">
    <div class="timeline-scale__tier timeline-scale__tier--top">
      <div
        v-for="cell in tiers.top"
        :key="cell.key"
        :class="['timeline-scale__group', { 'timeline-scale__group--today': cell.today }]"
        :style="{ left: `${cell.left}rem`, width: `${cell.width}rem` }"
      >
        <span class="timeline-scale__group-label">
          <span v-if="showWeekPrefix" class="timeline-scale__week-badge">{{ $t('common.week', 1) }} {{ cell.label }}</span>
          <template v-else>{{ cell.label }}</template>
          <span v-if="cell.caption" class="timeline-scale__group-caption">{{ cell.caption }}</span>
        </span>
      </div>
    </div>
    <div class="timeline-scale__tier timeline-scale__tier--bottom">
      <div
        v-for="cell in tiers.bottom"
        :key="cell.key"
        :class="[
          'timeline-scale__cell',
          {
            'timeline-scale__cell--compact': tiers.compact,
            'timeline-scale__cell--rest': cell.rest,
            'timeline-scale__cell--today': cell.today,
          },
        ]"
        :style="{ left: `${cell.left}rem`, width: `${cell.width}rem` }"
      >
        <span v-if="cell.caption && !tiers.compact" class="timeline-scale__caption">{{ cell.caption }}</span>
        <span class="timeline-scale__value">{{ cell.label }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.timeline-scale {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.timeline-scale__tier {
  position: relative;
}

.timeline-scale__tier--top {
  height: 1.625rem;
}

.timeline-scale__tier--bottom {
  flex: 1;
}

.timeline-scale__group {
  position: absolute;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  overflow: clip;
  border-left: 1px solid var(--timeline-line-strong);
}

.timeline-scale__group-label {
  position: sticky;
  left: var(--scale-sticky-left);
  display: inline-flex;
  align-items: baseline;
  gap: 0.5rem;
  padding: 0 0.625rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--timeline-text);
  white-space: nowrap;
  text-transform: capitalize;
}

.timeline-scale__group-caption {
  font-weight: 400;
  color: var(--timeline-text-muted);
  text-transform: none;
}

.timeline-scale__week-badge {
  text-transform: none;
}

.timeline-scale__group--today .timeline-scale__group-label {
  color: var(--q-primary);
}

.timeline-scale__cell {
  position: absolute;
  top: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  line-height: 1.1;
}

.timeline-scale__cell--rest {
  color: var(--timeline-text-muted);
}

.timeline-scale__caption {
  font-size: 0.625rem;
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--timeline-text-muted);
}

.timeline-scale__value {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.5rem;
  height: 1.5rem;
  padding: 0 0.25rem;
  border-radius: 1rem;
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--timeline-text);
}

.timeline-scale__cell--rest .timeline-scale__value {
  color: var(--timeline-text-muted);
}

.timeline-scale__cell--compact .timeline-scale__value {
  font-size: 0.6875rem;
  font-weight: 400;
  color: var(--timeline-text-muted);
}

.timeline-scale__cell--today .timeline-scale__value {
  background: var(--q-primary);
  color: white;
  font-weight: 600;
}

.timeline-scale__cell--today .timeline-scale__caption {
  color: var(--q-primary);
}
</style>
